<template>
  <div>
    <div :class="[containerClass, 'analytics-wide-container']">
      <page-header
        :title="'Course Analytics'"
        :container-class="null"
        :info-text="'Overview of course performance and student progress'"
        :show-back="true"
        class="mb-32pt"
      />
    </div>

    <div class="page-section">
      <div :class="[containerClass, 'analytics-wide-container']">
        <!-- Analytics Cards Section -->
        <div v-if="isLoadingAnalytics" class="text-center my-5">
          <b-spinner class="align-middle mr-2"></b-spinner>
          <strong>Loading analytics...</strong>
        </div>

        <div v-else class="analytics-cards-section mb-4">
          <div class="analytics-cards-grid">
            <div
              v-for="card in analyticsCardConfigs"
              :key="card.label"
              class="analytics-card"
            >
              <div class="analytics-card__header">
                <div :class="['analytics-card__icon', card.iconClass]">
                  <i class="material-icons">{{ card.icon }}</i>
                </div>
                <span class="analytics-card__label">{{ card.label }}</span>
              </div>
              <div class="analytics-card__value">
                {{ card.value }}
                <span v-if="card.suffix" class="analytics-card__suffix">{{ card.suffix }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Students Report Section -->
        <div class="students-report-section" ref="studentsSection">
          <div class="students-card mb-0">
            <div class="students-card__header">
              <div class="search-bar-wrapper">
                <input
                  type="text"
                  v-model="searchQuery"
                  @input="filterStudents"
                  placeholder="Search by student name or email..."
                  class="search-input"
                />
                <i class="material-icons search-icon">search</i>
              </div>
              <div class="filters-group">
                <v-select
                  class="form-control v-select-custom status-filter"
                  label="label"
                  v-model="statusFilter"
                  :reduce="option => option.value"
                  placeholder="All Status"
                  :options="statusOptions"
                  :clearable="true"
                  @input="filterStudents"
                >
                </v-select>
              </div>
            </div>
            <div class="students-card__body">
              <div v-if="!hasRequestedStudents" class="students-placeholder text-center py-5">
                <p class="text-muted mb-0">Student performance data loads when you reach this section.</p>
              </div>

              <div v-else-if="isLoadingStudents" class="text-center my-5">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Loading student reports...</strong>
              </div>

              <div v-else-if="filteredStudents.length" class="table-responsive students-table-wrapper">
                <table class="students-table">
                  <thead>
                    <tr>
                      <th>STUDENT</th>
                      <th>OVERALL SCORE</th>
                      <th>OVERALL PROGRESS</th>
                      <th>GRADE</th>
                      <th>STATUS</th>
                      <th>START DATE</th>
                      <th>LAST ACTIVE</th>
                      <th>END DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="student in filteredStudents" :key="student.id">
                      <td>
                        <div class="student-info">
                          <div class="student-name">{{ student.student_name }}</div>
                          <div class="student-email">{{ student.student_email }}</div>
                        </div>
                      </td>
                      <td>
                        <span class="score-value">{{ student.overall_score }}%</span>
                      </td>
                      <td>
                        <div class="progress-cell">
                          <div class="progress-bar-wrapper">
                            <div class="progress-bar-fill" :style="{ width: student.progress_percent + '%' }"></div>
                          </div>
                          <span class="progress-percent">{{ student.progress_percent }}%</span>
                        </div>
                      </td>
                      <td>
                        <span class="grade-badge" :class="getGradeClass(student.grade)">{{ student.grade }}</span>
                      </td>
                      <td>
                        <span class="status-badge" :class="getStatusClass(student.status)">{{ formatStatus(student.status) }}</span>
                      </td>
                      <td>{{ formatDate(student.start_date) }}</td>
                      <td>{{ formatDate(student.last_active) }}</td>
                      <td>{{ student.end_date ? formatDate(student.end_date) : '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-else class="text-center py-5">
                <p class="text-muted mb-0">No student reports found</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';

export default {
  components: {
    PageHeader,
  },
  extends: Page,

  data() {
    return {
      title: 'Course Analytics',
      courseId: null,
      analytics: {},
      students: [],
      filteredStudents: [],
      isLoadingAnalytics: true,
      isLoadingStudents: false,
      hasRequestedStudents: false,
      studentsObserver: null,
      courseName: '',
      searchQuery: '',
      statusFilter: null,
      statusOptions: [
        { label: 'All Status', value: null },
        { label: 'In Progress', value: 'in_progress' },
        { label: 'Completed', value: 'completed' },
        { label: 'Dropped', value: 'dropped' },
      ],
    };
  },

  computed: {
    analyticsPageTitle() {
      const name = this.courseName || 'Course';
      return `${name} Analytics`;
    },

    analyticsCardConfigs() {
      return [
        {
          label: 'Average Score',
          icon: 'trending_up',
          iconClass: 'analytics-card__icon--blue',
          value: this.formatPercent(this.analytics.average_score),
          suffix: '%',
        },
        {
          label: 'Highest Score',
          icon: 'emoji_events',
          iconClass: 'analytics-card__icon--green',
          value: this.formatInteger(this.analytics.highest_score),
          suffix: '%',
        },
        {
          label: 'Lowest Score',
          icon: 'trending_down',
          iconClass: 'analytics-card__icon--red',
          value: this.formatInteger(this.analytics.lowest_score),
          suffix: '%',
        },
        {
          label: 'Completion Rate',
          icon: 'check_circle',
          iconClass: 'analytics-card__icon--purple',
          value: this.formatPercent(this.analytics.completion_rate),
          suffix: '%',
        },
        {
          label: 'Total Students',
          icon: 'people',
          iconClass: 'analytics-card__icon--orange',
          value: this.formatInteger(this.analytics.total_students),
        },
      ];
    },
  },

  methods: {
    ...mapActions('courses', ['getCourseAnalytics', 'getCourseStudentsReport', 'getCourse']),

    async fetchCourseDetails() {
      try {
        const course = await this.getCourse(this.courseId);
        this.courseName = course?.title || 'Course';
      } catch (e) {
        this.courseName = 'Course';
      }
    },

    async fetchCourseAnalytics() {
      this.isLoadingAnalytics = true;
      try {
        const res = await this.getCourseAnalytics({ course_id: this.courseId });
        this.analytics = res.data || {};
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load course analytics' });
      }
      this.isLoadingAnalytics = false;
    },

    async fetchStudentsReport() {
      this.hasRequestedStudents = true;
      this.isLoadingStudents = true;
      try {
        const res = await this.getCourseStudentsReport({ course_id: this.courseId });
        this.students = res.data || [];
        this.filteredStudents = [...this.students];
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load student reports' });
      }
      this.isLoadingStudents = false;
    },

    setupStudentsLazyLoad() {
      if (!('IntersectionObserver' in window)) {
        this.fetchStudentsReport();
        return;
      }

      this.studentsObserver = new IntersectionObserver(entries => {
        const entry = entries[0];
        if (entry.isIntersecting && !this.hasRequestedStudents) {
          this.fetchStudentsReport();
          if (this.studentsObserver) {
            this.studentsObserver.disconnect();
            this.studentsObserver = null;
          }
        }
      }, { threshold: 0.1 });

      if (this.$refs.studentsSection) {
        this.studentsObserver.observe(this.$refs.studentsSection);
      }
    },

    formatPercent(value) {
      if (value === null || value === undefined || value === '') {
        return '0.00';
      }
      const num = Number(value);
      if (Number.isNaN(num)) return '0.00';
      return num.toFixed(2);
    },

    formatInteger(value) {
      if (value === null || value === undefined || value === '') {
        return '0';
      }
      return Number(value).toLocaleString();
    },

    filterStudents() {
      let filtered = [...this.students];
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(student => 
          student.student_name.toLowerCase().includes(query) ||
          student.student_email.toLowerCase().includes(query)
        );
      }
      
      // Filter by status
      if (this.statusFilter) {
        filtered = filtered.filter(student => student.status === this.statusFilter);
      }
      
      this.filteredStudents = filtered;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    },

    formatStatus(status) {
      return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    getGradeClass(grade) {
      const gradeMap = {
        'P': 'grade-a',
        'F': 'grade-f',
      };
      return gradeMap[grade] || '';
    },

    getStatusClass(status) {
      const statusMap = {
        'completed': 'status-completed',
        'in_progress': 'status-in-progress',
        'dropped': 'status-dropped',
      };
      return statusMap[status] || '';
    },
  },

  async mounted() {
    this.courseId = this.$route.params.id;
    if (!this.courseId) {
      this.makeToast({ variant: 'danger', msg: 'Course ID is required' });
      this.$router.go(-1);
      return;
    }
    await Promise.all([this.fetchCourseDetails(), this.fetchCourseAnalytics()]);
    this.setupStudentsLazyLoad();
  },

  beforeDestroy() {
    if (this.studentsObserver) {
      this.studentsObserver.disconnect();
    }
  },
};
</script>

<style scoped lang="scss">

.analytics-wide-container {
  max-width: 1420px;
}

.analytics-cards-section {
  margin-top: 2rem;
}

.analytics-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 18px;
}

.analytics-card {
  background: #fff;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  height: 100%;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 34px rgba(15, 23, 42, 0.12);
  }
}

.analytics-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.analytics-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 20px;
    color: #fff;
  }

  &--blue {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  &--green {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  }

  &--red {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  }

  &--purple {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  }

  &--orange {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  }
}

.analytics-card__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.analytics-card__value {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.analytics-card__suffix {
  font-size: 1.2rem;
  color: #6b7280;
}

.students-report-section {
  margin-top: 3rem;
}


.students-card {
  border: 1px solid #e7ecf5;
  border-radius: 16px;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
  background: #fff;
}

.students-card__header {
  padding: 24px;
  border-bottom: 1px solid #eef2fb;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.search-bar-wrapper {
  position: relative;
  flex: 0 0 360px;
  max-width: 360px;
  width: 100%;
  min-width: 280px;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #1f2937;
  transition: all 0.2s ease;
  background: #f8fafc;

  &:focus {
    outline: none;
    border-color: #667eea;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 20px;
  pointer-events: none;
}

.students-card__header .filters-group {
  margin-left: auto;
}

.students-card .card-header {
  border-bottom: 1px solid #eef2fb;
  padding: 18px 24px;
  background: #fff;
}

.students-table-wrapper {
  padding: 0 24px 24px;
}

.students-card__body {
  padding: 0;
}

.students-placeholder {
  padding: 32px 16px;
}

.section-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #111827;
}

.filters-group {
  display: flex;
  gap: 12px;
  align-items: center;
}

.status-filter {
  min-width: 180px;
}

@media (max-width: 991.98px) {
  .search-bar-wrapper {
    flex: 1;
    max-width: 100%;
  }
}

@media (max-width: 575.98px) {
  .students-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .search-bar-wrapper {
    width: 100%;
    min-width: 100%;
  }

  .students-card__header .filters-group {
    width: 100%;
    margin-left: 0;
  }

  .status-filter {
    width: 100%;
  }
}

.students-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.students-table thead {
  background: #f8fafc;
}

.students-table thead th {
  padding: 16px 20px;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
}

.students-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s ease;
}

.students-table tbody tr:hover {
  background-color: #f8fafc;
}

.students-table tbody tr:last-child {
  border-bottom: none;
}

.students-table tbody td {
  padding: 18px 20px;
  font-size: 0.95rem;
  color: #1f2937;
  vertical-align: middle;
}

.student-info {
  .student-name {
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 4px;
  }

  .student-email {
    font-size: 0.85rem;
    color: #6b7280;
  }
}

.score-cell {
  .score-value {
    font-weight: 600;
    color: #1f2937;
  }
}

.progress-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-bar-wrapper {
  width: 120px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-percent {
  font-weight: 600;
  color: #4b5563;
}

.grade-badge {
  display: inline-block;
  padding: 6px 16px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.9rem;
  text-align: center;
  min-width: 50px;

  &.grade-a {
    background: #d1fae5;
    color: #065f46;
  }

  &.grade-b {
    background: #dbeafe;
    color: #1e40af;
  }

  &.grade-c {
    background: #fef3c7;
    color: #92400e;
  }

  &.grade-d {
    background: #fed7aa;
    color: #9a3412;
  }

  &.grade-f {
    background: #fee2e2;
    color: #991b1b;
  }
}

.status-badge {
  display: inline-block;
  padding: 6px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: capitalize;

  &.status-completed {
    background: #d1fae5;
    color: #065f46;
  }

  &.status-in-progress {
    background: #dbeafe;
    color: #1e40af;
  }

  &.status-dropped {
    background: #fee2e2;
    color: #991b1b;
  }
}

@media (max-width: 1199.98px) {
  .analytics-card__value {
    font-size: 2rem;
  }
}

@media (max-width: 767.98px) {
  .analytics-card {
    padding: 20px;
  }

  .analytics-card__value {
    font-size: 1.75rem;
  }

  .students-table {
    font-size: 0.85rem;

    thead tr th,
    tbody tr td {
      padding: 12px 16px;
    }
  }

  .table-responsive {
    overflow-x: auto;
  }
}
</style>
